import { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { HomeProvider } from "@/context/HomeContent";
import { BookingProvider } from "@/context/Booking";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // In-page hash links go through Lenis; if not on home, route home first.
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length <= 1) return;
      e.preventDefault();
      if (location.pathname !== "/") {
        navigate("/" + id);
        return;
      }
      const el = document.querySelector(id);
      if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: -80 });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [location.pathname, navigate]);

  // Scroll to top (or to hash) on route change.
  useEffect(() => {
    const lenis = lenisRef.current;
    if (location.hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el && lenis) lenis.scrollTo(el, { offset: -80 });
      }, 120);
      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <HomeProvider>
      <BookingProvider>
        <div className="App bg-brand-cream text-brand-ink" data-testid="app-root">
          <Navbar />
          <main>
            <Outlet />
          </main>
          <Footer />
          <WhatsAppButton />
        </div>
      </BookingProvider>
    </HomeProvider>
  );
};

export default Layout;
